import { Customer, CustomerMonthlyStatus } from '../types';

export const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

/**
 * Berechnet alle NRW-Feiertage für ein gegebenes Jahr
 */
export const getNRWHolidays = (year: number): Date[] => {
  const holidays: Date[] = [];
  
  // Feste Feiertage
  holidays.push(new Date(year, 0, 1));   // Neujahr
  holidays.push(new Date(year, 4, 1));   // Tag der Arbeit
  holidays.push(new Date(year, 9, 3));   // Tag der Deutschen Einheit
  holidays.push(new Date(year, 10, 1));  // Allerheiligen
  holidays.push(new Date(year, 11, 25)); // 1. Weihnachtstag
  holidays.push(new Date(year, 11, 26)); // 2. Weihnachtstag
  
  // Variable Feiertage (basierend auf Ostersonntag)
  const easter = calculateEasterSunday(year);
  
  holidays.push(new Date(easter.getTime() - 2 * 24 * 60 * 60 * 1000)); // Karfreitag
  holidays.push(new Date(easter.getTime() + 1 * 24 * 60 * 60 * 1000)); // Ostermontag
  holidays.push(new Date(easter.getTime() + 39 * 24 * 60 * 60 * 1000)); // Christi Himmelfahrt
  holidays.push(new Date(easter.getTime() + 50 * 24 * 60 * 60 * 1000)); // Pfingstmontag
  holidays.push(new Date(easter.getTime() + 60 * 24 * 60 * 60 * 1000)); // Fronleichnam
  
  return holidays;
};

/**
 * Berechnet den Ostersonntag für ein gegebenes Jahr (Gaußsche Osterformel)
 */
const calculateEasterSunday = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  
  return new Date(year, month - 1, day);
};

/**
 * Prüft ob ein Datum ein NRW-Feiertag ist
 */
export const isNRWHoliday = (date: Date): boolean => {
  const year = date.getFullYear();
  const holidays = getNRWHolidays(year);
  
  return holidays.some(holiday => 
    holiday.getDate() === date.getDate() &&
    holiday.getMonth() === date.getMonth() &&
    holiday.getFullYear() === date.getFullYear()
  );
};

/**
 * Prüft ob ein Datum ein Wochenende oder NRW-Feiertag ist
 */
export const isHolidayOrWeekendNRW = (date: Date): boolean => {
  return isWeekend(date) || isNRWHoliday(date);
};

/**
 * Verschiebt ein Datum zum nächsten Werktag (berücksichtigt Wochenenden und NRW-Feiertage)
 */
export const adjustToNextWorkday = (date: Date): Date => {
  const adjusted = new Date(date);
  while (isHolidayOrWeekendNRW(adjusted)) {
    adjusted.setDate(adjusted.getDate() + 1);
  }
  return adjusted;
};

/**
 * Verschiebt ein Datum zum vorherigen Werktag (berücksichtigt Wochenenden und NRW-Feiertage)
 */
export const adjustToPreviousWorkday = (date: Date): Date => {
  const adjusted = new Date(date);
  while (isHolidayOrWeekendNRW(adjusted)) {
    adjusted.setDate(adjusted.getDate() - 1);
  }
  return adjusted;
};

/**
 * Zählt ausgehend von einem Datum eine Anzahl Werktage vorwärts (workdays > 0)
 * oder rückwärts (workdays < 0) und überspringt dabei Wochenenden und
 * NRW-Feiertage. Das Ergebnis ist damit immer ein Werktag.
 *
 * Grundlage für relative Fristen mit Werktage-Bezug, z.B. "1 Werktag nach dem
 * ersten Lohnlauf": Der Anker ist der effektive Lohnlauf-Termin, der Offset ist
 * die Anzahl Werktage relativ dazu. Bei workdays = 0 wird der Anker selbst
 * zurückgegeben, sofern er ein Werktag ist, andernfalls der nächste Werktag.
 */
export const addWorkdays = (date: Date, workdays: number): Date => {
  const result = new Date(date);
  if (workdays === 0) {
    return adjustToNextWorkday(result);
  }
  const step = workdays > 0 ? 1 : -1;
  let remaining = Math.abs(workdays);
  while (remaining > 0) {
    result.setDate(result.getDate() + step);
    if (!isHolidayOrWeekendNRW(result)) {
      remaining--;
    }
  }
  return result;
};

export const calculateLohnlaufDate = (
  year: number, 
  month: number, 
  day: number, 
  verschiebung: string | null,
  monatOffset: number | null = 0
): Date => {
  // Berechne Zielmonat mit Offset
  const effectiveOffset = monatOffset || 0;
  let targetYear = year;
  let targetMonth = month + effectiveOffset;
  
  // Handle month overflow/underflow
  while (targetMonth > 12) {
    targetYear++;
    targetMonth -= 12;
  }
  while (targetMonth < 1) {
    targetYear--;
    targetMonth += 12;
  }
  
  // Erstelle Datum im Zielmonat
  const date = new Date(targetYear, targetMonth - 1, day);
  
  // Anwende Verschiebung nur wenn nötig
  if (verschiebung === 'frueher') {
    // NUR verschieben wenn das Datum auf Wochenende oder Feiertag fällt
    if (isHolidayOrWeekendNRW(date)) {
      return adjustToPreviousWorkday(date);
    }
    return date; // Werktag bleibt unverändert
  } else if (verschiebung === 'spaeter') {
    // NUR verschieben wenn das Datum auf Wochenende oder Feiertag fällt
    if (isHolidayOrWeekendNRW(date)) {
      return adjustToNextWorkday(date);
    }
    return date; // Werktag bleibt unverändert
  }
  
  return date;
};

/**
 * SSoT FÜR LOHNLAUF-FRISTEN (Feature: Frist ab Datenanlieferung)
 *
 * Alle Verbraucher (Fristenboard, Morning-Modal, Ticket-Sidebar, Heatmap)
 * berechnen Lohnlauf-Fristen ausschließlich über diese Funktion.
 *
 * Logik:
 * - Kein Lohnlauf-Tag konfiguriert → undefined (Frist existiert nicht)
 * - Toggle "Frist ab Anlieferung" AUS → fixer Termin via calculateLohnlaufDate
 *   (bisheriges Verhalten, unverändert)
 * - Toggle AN, aber keine Anlieferung im Monatsstatus markiert → fixer Termin
 *   als Erinnerungsdatum (eskaliert wie bisher, falls der Kunde keine Daten schickt)
 * - Toggle AN und Anlieferung markiert → Frist = nächster NRW-Werktag nach dem
 *   Anlieferungstag (Anlieferungstag + 1 Tag, dann adjustToNextWorkday).
 *   Beispiel: Anlieferung Freitag → Frist Montag (bzw. Dienstag bei Feiertagsmontag).
 *   Gilt auch, wenn die Anlieferung erst nach dem fixen Termin erfolgt.
 * - Toggle AN, Anlieferung markiert und Kundenrückmeldung markiert (Feature
 *   "Wartet auf Kunden") → Frist = nächster NRW-Werktag nach dem
 *   Rückmeldungstag. Solange die Abrechnung beim Kunden liegt, ruht die Frist;
 *   das hier gelieferte Datum wird dann nicht als fällig gewertet (siehe
 *   ermittleRuhendeFristen in deadlineUtils).
 *
 * @param customer - Kunde mit Lohnlauf-Stammdaten und Toggles
 * @param lohnlauf - 1 = erster Lohnlauf, 2 = zweiter Lohnlauf
 * @param year - Abrechnungsjahr (Arbeitsmonat)
 * @param month - Abrechnungsmonat 1-12 (Arbeitsmonat)
 * @param status - Monatsstatus des Kunden für diesen Arbeitsmonat (oder null)
 */
export const calculateEffectiveLohnlaufDate = (
  customer: Customer,
  lohnlauf: 1 | 2,
  year: number,
  month: number,
  status: CustomerMonthlyStatus | null
): Date | undefined => {
  const tag = lohnlauf === 1 ? customer.erster_lohnlauf_tag : customer.zweiter_lohnlauf_tag;
  if (!tag) return undefined;

  const verschiebung = lohnlauf === 1
    ? customer.erster_lohnlauf_verschiebung
    : customer.zweiter_lohnlauf_verschiebung;
  const monatOffset = lohnlauf === 1
    ? customer.erster_lohnlauf_monat_offset
    : customer.zweiter_lohnlauf_monat_offset;
  const fristAbAnlieferung = lohnlauf === 1
    ? customer.erster_lohnlauf_frist_ab_anlieferung
    : customer.zweiter_lohnlauf_frist_ab_anlieferung;
  const fieldName = lohnlauf === 1 ? 'lohnlauf_1_completed' : 'lohnlauf_2_completed';

  // Dynamische Frist: Toggle aktiv UND Anlieferung im Monatsstatus markiert
  const anlieferungInfo = status?.daten_anlieferungen?.[fieldName];
  if (fristAbAnlieferung && anlieferungInfo?.angeliefert_am) {
    // WARTET AUF KUNDEN: Liegt eine Kundenrückmeldung vor, läuft die Frist ab
    // dem Rückmeldungstag neu, sonst ab dem Anlieferungstag.
    const basisTag = anlieferungInfo.rueckmeldung_am || anlieferungInfo.angeliefert_am;
    // 'T00:00:00' erzwingt lokale Zeitzone (gleiche Konvention wie Snooze-Feature)
    const basisDatum = new Date(basisTag + 'T00:00:00');
    const naechsterTag = new Date(basisDatum);
    naechsterTag.setDate(naechsterTag.getDate() + 1);
    return adjustToNextWorkday(naechsterTag);
  }

  // Fixer Termin (bisheriges Verhalten; bei aktivem Toggle als Erinnerungsdatum)
  return calculateLohnlaufDate(year, month, tag, verschiebung || null, monatOffset || null);
};

/**
 * SSoT FÜR DIE LOHNSCHEIN-VERSAND-FRIST (Stufe 3.2, Folgemonat-Modell)
 *
 * Die Frist gehört zum ARBEITSMONAT des Kunden, bezieht sich aber inhaltlich
 * auf den VORMONAT (Versand-Periode): Die Lohnscheine für Periode M können
 * erst existieren, wenn die Abrechnung für M finalisiert ist — also im
 * Arbeitsmonat M+1. Entspricht der bisher manuell gepflegten individuellen
 * Frist "Versand Lohnscheine Vormonat".
 *
 * Logik:
 * - Relative Frist (bezug_lohnlauf gesetzt): Anker ist der effektive Lohnlauf
 *   der Versand-Periode (Vormonat), plus Werktage-Offset. Fehlt der referenzierte
 *   Lohnlauf beim Kunden, liefert die Frist bewusst kein Datum. Der Anker folgt
 *   einer ggf. markierten Datenanlieferung dieser Periode (bezugStatus).
 * - Fester Tag (bezug_lohnlauf NULL): Tag im Arbeitsmonat, Verschiebung und
 *   Monat-Offset wie bei den Lohnlauf-Fristen über calculateLohnlaufDate.
 * - Kein Tag und kein Bezug konfiguriert → undefined (Frist existiert nicht)
 * - Die Frist fließt NICHT in fully_completed ein und wird bei der
 *   Finalisierung nicht automatisch erledigt
 *
 * @param customer - Kunde mit Lohnschein-Versand-Stammdaten
 * @param year - Arbeitsjahr (Arbeitsmonat des Kunden)
 * @param month - Arbeitsmonat 1-12
 * @param bezugStatus - Monatsstatus der Versand-Periode (Vormonat) für die
 *   Anlieferungskopplung des Ankers; null in der Grundlast-Sicht (Heatmap)
 */
export const calculateLohnscheinVersandDate = (
  customer: Customer,
  year: number,
  month: number,
  bezugStatus: CustomerMonthlyStatus | null = null
): Date | undefined => {
  if (customer.lohnschein_versand_bezug_lohnlauf) {
    const periode = getLohnscheinPeriode(year, month);
    const anker = calculateEffectiveLohnlaufDate(
      customer,
      customer.lohnschein_versand_bezug_lohnlauf as 1 | 2,
      periode.year,
      periode.month,
      bezugStatus
    );
    if (!anker) return undefined;
    return addWorkdays(anker, customer.lohnschein_versand_bezug_werktage_offset ?? 0);
  }

  if (!customer.lohnschein_versand_tag) return undefined;
  return calculateLohnlaufDate(
    year,
    month,
    customer.lohnschein_versand_tag,
    customer.lohnschein_versand_verschiebung || null,
    customer.lohnschein_versand_monat_offset || null
  );
};

/**
 * Versand-Periode der Lohnschein-Frist: der Vormonat des Arbeitsmonats.
 * Wird für die Titel-Anzeige ("Lohnschein-Versand (für MM/JJJJ)") genutzt;
 * Baustein C sucht die DATEV-Dokumente (LOBN/LSTB/SVNW) für genau diese Periode.
 */
export const getLohnscheinPeriode = (
  year: number,
  month: number
): { year: number; month: number } => {
  if (month === 1) {
    return { year: year - 1, month: 12 };
  }
  return { year, month: month - 1 };
};

/**
 * SSoT FÜR DIE LOHNSCHEIN-VERSAND-FRIST PRO LOHNLAUF (Stufe 3.2, Zwei-Lauf-Modell)
 *
 * Berechnet für einen konfigurierten Lohnlauf sowohl das Fälligkeitsdatum der
 * Kachel als auch den Inhaltsmonat (Abrechnungsmonat) der zu versendenden
 * Lohnscheine. Ersetzt für das Zwei-Lauf-Modell die feste Vormonats-Annahme von
 * calculateLohnscheinVersandDate/getLohnscheinPeriode (die bleiben unverändert
 * für den bisherigen Ein-Platz-Pfad bestehen).
 *
 * Regel für den Inhaltsmonat (bestätigt 22.07.2026):
 *   Inhaltsmonat = nominaler Fälligkeits-Kalendermonat minus Monat-Versatz des
 *   zugeordneten Lohnlaufs.
 *   - Relativer Lauf (bezug_werktage_offset gesetzt): Anker ist der effektive
 *     Lohnlauf-Termin für den Arbeitsmonat; der Lohnlauf-Versatz steckt schon im
 *     Fälligkeitsmonat und hebt sich heraus, Inhalt = Arbeitsmonat.
 *   - Fester Tag: Fälligkeitsmonat = Arbeitsmonat + config.monat_offset; Inhalt =
 *     Arbeitsmonat + config.monat_offset minus Lohnlauf-Versatz. Ein Bestandskunde
 *     mit Lohnlauf-Versatz 1 bleibt damit beim Vormonat.
 *
 * Liefert faelligkeit=undefined, wenn der Lauf nicht datierbar ist (kein Tag,
 * kein Anker). Die Periode dient dann nur der Anzeige (Vormonat als Rückfall).
 */
export interface LohnscheinVersandLaufConfig {
  lohnlauf: 1 | 2;
  tag: number | null;
  verschiebung: string | null;
  monat_offset: number | null;
  bezug_werktage_offset: number | null;
}

export const calculateLohnscheinVersandForLauf = (
  customer: Customer,
  config: LohnscheinVersandLaufConfig,
  year: number,
  month: number,
  bezugStatus: CustomerMonthlyStatus | null = null
): { faelligkeit: Date | undefined; periode: { year: number; month: number } } => {
  const lohnlaufOffset = (config.lohnlauf === 1
    ? customer.erster_lohnlauf_monat_offset
    : customer.zweiter_lohnlauf_monat_offset) || 0;

  let faelligkeit: Date | undefined;
  let nominalerFaelligkeitsOffset: number;

  const relativ = config.bezug_werktage_offset !== null && config.bezug_werktage_offset !== undefined;
  if (relativ) {
    const anker = calculateEffectiveLohnlaufDate(customer, config.lohnlauf, year, month, bezugStatus);
    if (!anker) {
      return { faelligkeit: undefined, periode: getLohnscheinPeriode(year, month) };
    }
    faelligkeit = addWorkdays(anker, config.bezug_werktage_offset as number);
    nominalerFaelligkeitsOffset = lohnlaufOffset;
  } else {
    if (!config.tag) {
      return { faelligkeit: undefined, periode: getLohnscheinPeriode(year, month) };
    }
    faelligkeit = calculateLohnlaufDate(year, month, config.tag, config.verschiebung || null, config.monat_offset || null);
    nominalerFaelligkeitsOffset = config.monat_offset || 0;
  }

  const inhaltsOffset = nominalerFaelligkeitsOffset - lohnlaufOffset;
  let cy = year;
  let cm = month + inhaltsOffset;
  while (cm > 12) { cy++; cm -= 12; }
  while (cm < 1) { cy--; cm += 12; }
  return { faelligkeit, periode: { year: cy, month: cm } };
};

/**
 * KONFIGURATION EINER PERIODENBEZOGENEN SPEZIALFRIST
 *
 * Fasst die fünf Stammdatenfelder zusammen, die eine der beiden OPPM-QR-Fristen
 * beschreiben. Bewusst als eigener Typ, damit die gemeinsame Berechnung nur
 * einmal existiert und beide Fristen garantiert dieselbe Regel anwenden.
 */
export interface SpezialfristConfig {
  bezug_lohnlauf: number | null;
  bezug_werktage_offset: number | null;
  tag: number | null;
  verschiebung: string | null;
  monat_offset: number | null;
}

/**
 * SSoT FÜR PERIODENBEZOGENE SPEZIALFRISTEN (Fälligkeit UND Abrechnungsperiode)
 *
 * Liefert zu einer Spezialfrist beides: das Fälligkeitsdatum der Kachel und den
 * Abrechnungsmonat, auf den sich die Frist inhaltlich bezieht.
 *
 * RELATIVE FRIST (bezug_lohnlauf gesetzt)
 * Anker ist der effektive Lohnlauf des ARBEITSMONATS, nicht der des Vormonats.
 * Damit gilt dieselbe Regel wie beim Lohnschein-Versand
 * (calculateLohnscheinVersandForLauf): Inhaltsmonat = nominaler Fälligkeits-
 * Kalendermonat minus Monat-Versatz des zugeordneten Lohnlaufs. Im relativen
 * Zweig steckt der Lohnlauf-Versatz bereits im Fälligkeitsmonat und hebt sich
 * dadurch heraus; der Inhaltsmonat ist also immer der Arbeitsmonat selbst.
 *
 * Warum das korrigiert wurde: Zuvor war der Anker der Lohnlauf der VORMONATS-
 * Periode. Bei Standorten, deren Bezugs-Lohnlauf selbst einen Monat-Versatz hat
 * (OPPM: zweiter Lohnlauf am 10. des Folgemonats), fiel die Kachel für Periode M
 * dadurch auf genau den Tag, an dem M finalisiert wird und der Arbeitsmonat auf
 * M+1 springt. Die Frist konnte deshalb frühestens an ihrem eigenen
 * Fälligkeitstag überhaupt sichtbar werden.
 *
 * FESTER TAG (bezug_lohnlauf NULL)
 * Unverändertes Vormonat-Modell: Tag, Verschiebung und Monat-Offset im
 * Arbeitsmonat über calculateLohnlaufDate, Periode ist der Vormonat. Dieser
 * Zweig wurde bewusst nicht angefasst, damit Standorte ohne Lohnlauf-Bezug ihr
 * bisheriges Verhalten exakt behalten.
 *
 * Ist weder ein Tag noch ein Bezug konfiguriert, gibt es keine Frist; die
 * Periode dient dann nur der Anzeige.
 *
 * @param customer - Kunde mit Lohnlauf- und Spezialfrist-Stammdaten
 * @param config - Stammdaten der konkreten Frist
 * @param year - Arbeitsjahr des Kunden
 * @param month - Arbeitsmonat 1-12
 * @param ankerStatus - Monatsstatus des ARBEITSMONATS. Er liefert dem Anker eine
 *   ggf. markierte Datenanlieferung. Im festen Zweig ungenutzt.
 */
export const calculateSpezialfristMitPeriode = (
  customer: Customer,
  config: SpezialfristConfig,
  year: number,
  month: number,
  ankerStatus: CustomerMonthlyStatus | null = null
): { faelligkeit: Date | undefined; periode: { year: number; month: number } } => {
  if (config.bezug_lohnlauf) {
    const anker = calculateEffectiveLohnlaufDate(
      customer,
      config.bezug_lohnlauf as 1 | 2,
      year,
      month,
      ankerStatus
    );
    if (!anker) {
      return { faelligkeit: undefined, periode: { year, month } };
    }
    return {
      faelligkeit: addWorkdays(anker, config.bezug_werktage_offset ?? 0),
      periode: { year, month }
    };
  }

  const periode = getLohnscheinPeriode(year, month);
  if (!config.tag) {
    return { faelligkeit: undefined, periode };
  }
  return {
    faelligkeit: calculateLohnlaufDate(
      year,
      month,
      config.tag,
      config.verschiebung || null,
      config.monat_offset || null
    ),
    periode
  };
};

/** Stammdaten der OPPM-QR-Versand-Frist als SpezialfristConfig. */
export const oppmQrVersandConfig = (customer: Customer): SpezialfristConfig => ({
  bezug_lohnlauf: customer.oppm_qr_versand_bezug_lohnlauf ?? null,
  bezug_werktage_offset: customer.oppm_qr_versand_bezug_werktage_offset ?? null,
  tag: customer.oppm_qr_versand_tag ?? null,
  verschiebung: customer.oppm_qr_versand_verschiebung ?? null,
  monat_offset: customer.oppm_qr_versand_monat_offset ?? null
});

/** Stammdaten der OPPM-Monatsübersicht-QR-Frist als SpezialfristConfig. */
export const monatsuebersichtQrVersandConfig = (customer: Customer): SpezialfristConfig => ({
  bezug_lohnlauf: customer.monatsuebersicht_qr_versand_bezug_lohnlauf ?? null,
  bezug_werktage_offset: customer.monatsuebersicht_qr_versand_bezug_werktage_offset ?? null,
  tag: customer.monatsuebersicht_qr_versand_tag ?? null,
  verschiebung: customer.monatsuebersicht_qr_versand_verschiebung ?? null,
  monat_offset: customer.monatsuebersicht_qr_versand_monat_offset ?? null
});

/**
 * OPPM-QR-VERSAND-FRIST: Fälligkeit und Abrechnungsperiode.
 *
 * Die Frist fließt NICHT in fully_completed ein und wird bei der Finalisierung
 * nicht automatisch erledigt.
 */
export const calculateOppmQrVersandMitPeriode = (
  customer: Customer,
  year: number,
  month: number,
  ankerStatus: CustomerMonthlyStatus | null = null
): { faelligkeit: Date | undefined; periode: { year: number; month: number } } =>
  calculateSpezialfristMitPeriode(customer, oppmQrVersandConfig(customer), year, month, ankerStatus);

/**
 * OPPM-MONATSÜBERSICHT-QR-FRIST: Fälligkeit und Abrechnungsperiode.
 */
export const calculateMonatsuebersichtQrVersandMitPeriode = (
  customer: Customer,
  year: number,
  month: number,
  ankerStatus: CustomerMonthlyStatus | null = null
): { faelligkeit: Date | undefined; periode: { year: number; month: number } } =>
  calculateSpezialfristMitPeriode(customer, monatsuebersichtQrVersandConfig(customer), year, month, ankerStatus);

/**
 * OPPM-QR-Versand: nur die Fälligkeit. Dünner Aufsatz auf
 * calculateOppmQrVersandMitPeriode für Aufrufstellen, die keine Periode
 * brauchen (Signatur unverändert gegenüber der früheren Fassung).
 */
export const calculateOppmQrVersandDate = (
  customer: Customer,
  year: number,
  month: number,
  ankerStatus: CustomerMonthlyStatus | null = null
): Date | undefined =>
  calculateOppmQrVersandMitPeriode(customer, year, month, ankerStatus).faelligkeit;

/**
 * OPPM-Monatsübersicht-QR-Versand: nur die Fälligkeit.
 */
export const calculateMonatsuebersichtQrVersandDate = (
  customer: Customer,
  year: number,
  month: number,
  ankerStatus: CustomerMonthlyStatus | null = null
): Date | undefined =>
  calculateMonatsuebersichtQrVersandMitPeriode(customer, year, month, ankerStatus).faelligkeit;

/**
 * Versand-Periode der OPPM-QR-Frist im Vormonat-Modell.
 *
 * ACHTUNG: Diese Ableitung gilt nur noch für Fristen mit FESTEM TAG. Bei
 * relativem Lohnlauf-Bezug ist die Periode der Arbeitsmonat selbst. Neue
 * Aufrufstellen nehmen deshalb calculateOppmQrVersandMitPeriode und lesen die
 * Periode dort ab, statt sie hier abzuleiten.
 */
export const getOppmQrPeriode = (
  year: number,
  month: number
): { year: number; month: number } => getLohnscheinPeriode(year, month);

/**
 * Versand-Periode der OPPM-Monatsübersicht-QR-Frist im Vormonat-Modell.
 * Gleicher Vorbehalt wie bei getOppmQrPeriode.
 */
export const getMonatsuebersichtQrPeriode = (
  year: number,
  month: number
): { year: number; month: number } => getLohnscheinPeriode(year, month);

/**
 * ERWEITERTE FUNKTION: Berechnet die korrekte Lohnsteuerfrist basierend auf lohnsteuer_zeitraum
 * - monatlich: Jeden Monat (Standard)
 * - monatlich_versetzt: Frist im Folgemonat
 * - vierteljaehrlich: Nur März, Juni, September, Dezember
 * - jaehrlich: Nur Dezember
 * - keine: Keine Lohnsteuerfrist
 */
export const calculateLohnsteuerDeadline = (
  abrechnungsYear: number,
  abrechnungsMonth: number,
  lohnsteuerZeitraum: string | null,
  deadlinesData: Array<{ year: number; month: number; lohnsteuer_frist: string | null }>
): Date | null => {
  // Bei "keine" oder null keine Frist zurückgeben
  if (!lohnsteuerZeitraum || lohnsteuerZeitraum === 'keine') {
    return null;
  }
  
  // Bei "vierteljaehrlich" nur in bestimmten Monaten
  if (lohnsteuerZeitraum === 'vierteljaehrlich') {
    // Nur März (3), Juni (6), September (9) und Dezember (12)
    if (![3, 6, 9, 12].includes(abrechnungsMonth)) {
      return null;
    }
  }
  
  // Bei "jaehrlich" nur im Dezember
  if (lohnsteuerZeitraum === 'jaehrlich') {
    if (abrechnungsMonth !== 12) {
      return null;
    }
  }
  
  // Bestimme Zielmonat für die Frist
  let targetYear = abrechnungsYear;
  let targetMonth = abrechnungsMonth;
  
  // Bei "monatlich_versetzt" den Folgemonat verwenden
  if (lohnsteuerZeitraum === 'monatlich_versetzt') {
    if (targetMonth === 12) {
      targetYear = targetYear + 1;
      targetMonth = 1;
    } else {
      targetMonth = targetMonth + 1;
    }
  }
  // Bei allen anderen Zeiträumen (monatlich, vierteljaehrlich, jaehrlich)
  // wird die normale Frist des aktuellen Monats verwendet
  
  // Finde die entsprechende Deadline
  const deadline = deadlinesData.find(d => 
    d.year === targetYear && 
    d.month === targetMonth
  );
  
  if (deadline?.lohnsteuer_frist) {
    return new Date(deadline.lohnsteuer_frist);
  }
  
  return null;
};

export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('de-DE', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric' 
  });
};

/**
 * Berechnet den aktuellen Arbeitsmonat eines Kunden basierend auf seinen Status-Einträgen.
 * Extrahiert aus useDeadlines.ts für Shared-Nutzung (Urlaubsplaner Heatmap, etc.)
 * 
 * Logik:
 * - Kein Status → aktueller Kalendermonat
 * - Neuester Status fully_completed → Folgemonat
 * - Neuester Status NICHT fully_completed → Monat aus Status
 */
export const getCurrentWorkingMonth = (customer: Customer, statuses: CustomerMonthlyStatus[]): { year: number; month: number } => {
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth() + 1;

  // Alle Status-Einträge für den Kunden laden (completed und nicht-completed)
  const customerStatuses = statuses.filter(s => s.customer_id === customer.id);
  
  if (customerStatuses.length > 0) {
    // Sortiere nach neuestem Datum
    customerStatuses.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
    
    const latestStatus = customerStatuses[0];
    
    if (latestStatus.fully_completed) {
      // Abgeschlossen → Nächster Monat
      if (latestStatus.month === 12) {
        year = latestStatus.year + 1;
        month = 1;
      } else {
        year = latestStatus.year;
        month = latestStatus.month + 1;
      }
    } else {
      // Nicht abgeschlossen → Aktueller Monat aus DB
      year = latestStatus.year;
      month = latestStatus.month;
    }
  }
  // Fallback: Aktueller Monat (wenn gar kein Status-Eintrag existiert)

  return { year, month };
};

/**
 * GEGENSTÜCK ZU getCurrentWorkingMonth: der zuletzt ABGESCHLOSSENE Monat.
 *
 * Definition: der Kalendermonat DIREKT VOR dem Arbeitsmonat, und nur dann,
 * wenn für ihn eine Statuszeile mit fully_completed existiert. Der Arbeitsmonat
 * ist damit der einzige Anker; beide Funktionen können nicht auseinanderdriften,
 * und die Regel "genau ein Monat zurück" steht explizit im Code.
 *
 * WICHTIG (korrigiert 14.08.2026): Es genügt NICHT, die neueste Statuszeile zu
 * prüfen und sie bei fully_completed zurückzugeben. Sobald für den Folgemonat
 * bereits eine Zeile existiert, ist die neueste Zeile der Arbeitsmonat selbst
 * und damit nicht abgeschlossen — der tatsächlich abgeschlossene Monat liegt
 * eine Zeile darunter. Solche Folgemonatszeilen entstehen im Regelbetrieb
 * laufend: beim erkannten Folgemonats-Beitragsnachweis in der Finalisierung,
 * beim Snooze und beim Markieren einer Datenanlieferung. Die frühere Fassung
 * lieferte dadurch bei fast allen Kunden null.
 *
 * Verwendung: Nachzügler-Kacheln für Fristen, die zum Zeitpunkt der
 * Finalisierung noch offen waren (siehe LohnscheinNachzuegler in types).
 *
 * Logik:
 * - Keine Statuszeile für den Vormonat → null
 * - Vormonatszeile vorhanden, aber nicht fully_completed → null
 * - Vormonatszeile vorhanden und fully_completed → genau dieser Monat
 *
 * @param customer - Kunde
 * @param statuses - Status-Einträge (dürfen alle Kunden enthalten, wird gefiltert)
 */
export const getLastCompletedMonth = (
  customer: Customer,
  statuses: CustomerMonthlyStatus[]
): { year: number; month: number } | null => {
  const arbeitsmonat = getCurrentWorkingMonth(customer, statuses);

  // Kalendermonat direkt vor dem Arbeitsmonat
  const vorJahr = arbeitsmonat.month === 1 ? arbeitsmonat.year - 1 : arbeitsmonat.year;
  const vorMonat = arbeitsmonat.month === 1 ? 12 : arbeitsmonat.month - 1;

  const vorStatus = statuses.find(s =>
    s.customer_id === customer.id &&
    s.year === vorJahr &&
    s.month === vorMonat
  );

  if (!vorStatus || !vorStatus.fully_completed) return null;

  return { year: vorJahr, month: vorMonat };
};
